import React, { MouseEventHandler, ReactElement, ReactNode, Ref, cloneElement } from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import type { TouchRippleActions } from "@mui/material/ButtonBase/TouchRipple";
import { Link } from "react-router-dom";
import type { SxProps, Theme } from "@mui/material/styles";
import { ACTIONS_TEXT_BUTTON_SX } from "./DataTableContainer";

interface DataGridActionsLinkButtonSharedProps {
  /** Relative path to the row's detail page — typically just the row id. */
  to: string;
  /** Router location state to carry onto the detail page (e.g. the list's current page/sort for a "back" link). */
  state?: unknown;
  /**
   * Passed through to react-router's `Link`. Use for a same-page state change (e.g. CurbPass
   * Transactions, which reads its "detail view" from a `transactionId` search param on the same
   * route) so clicking doesn't add a history entry. Defaults to false, matching `Link`'s own default.
   */
  replace?: boolean;
  /**
   * `tabIndex`/`onClick`/`touchRippleRef` are cloned onto this element by DataGridPro's actions-cell
   * renderer (for roving-tabindex keyboard navigation and ripple cleanup) when used in a `getActions`
   * column — forward them to the underlying `Button`/`IconButton` rather than dropping them.
   */
  tabIndex?: number;
  onClick?: MouseEventHandler;
  touchRippleRef?: Ref<TouchRippleActions>;
}

export interface DataGridActionsLinkIconButtonProps extends DataGridActionsLinkButtonSharedProps {
  icon: ReactElement;
  /** Accessible label — mirrors GridActionsCellItem's required `label`. */
  label: string;
  children?: never;
  variant?: never;
  fullWidth?: never;
  sx?: never;
}

export interface DataGridActionsLinkTextButtonProps extends DataGridActionsLinkButtonSharedProps {
  icon?: never;
  label?: never;
  children: ReactNode;
  variant?: ButtonProps["variant"];
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export type DataGridActionsLinkButtonProps = DataGridActionsLinkIconButtonProps | DataGridActionsLinkTextButtonProps;

/**
 * An action-column button (e.g. "Review", or the chevron "View Citation"/"View Invoice") that
 * renders as a real anchor via react-router's `Link`, so right-click/cmd-click "open in new tab"
 * works — unlike a plain `onClick` + `navigate()`. Pass `icon`+`label` for the icon-button variant,
 * which reproduces GridActionsCellItem's icon-button render directly (size, role, aria-label) since
 * its own TS type doesn't expose `component`/`to`; pass `children` for the text-button variant.
 */
export function DataGridActionsLinkButton(props: DataGridActionsLinkButtonProps): JSX.Element {
  if ("icon" in props && props.icon) {
    const { icon, label, ...rest } = props;
    return (
      <IconButton component={Link} size="small" role="menuitem" aria-label={label} {...rest}>
        {cloneElement(icon, { fontSize: "small" })}
      </IconButton>
    );
  }

  const { children, variant = "text", fullWidth = false, sx = ACTIONS_TEXT_BUTTON_SX, ...rest } = props;
  return (
    <Button component={Link} color="primary" variant={variant} fullWidth={fullWidth} sx={sx} {...rest}>
      {children}
    </Button>
  );
}
